import { useParams, Link } from "react-router-dom";
import { categoryVideos } from "@/mocks/articles";
import { categories } from "@/mocks/home";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryVideos[slug] : null;
  const categoryInfo = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mb-4">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
        </div>
        <h2 className="font-heading text-xl font-semibold text-foreground-800 mb-2">分类未找到</h2>
        <p className="text-foreground-500 text-sm mb-6">该分类不存在或已下架</p>
        <Link
          to="/"
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const catSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.title} - 17.c 起草在线观看免费版`,
    description: category.description,
    url: `https://17c-drafting.example.com/category/${slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "17.c 起草在线观看免费版",
      url: "https://17c-drafting.example.com",
    },
  };

  return (
    <main className="pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(catSchema) }} />
      <Breadcrumb
        items={[
          { label: "首页", href: "/" },
          { label: category.title },
        ]}
      />

      <section className="py-8 md:py-10 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {category.title}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-[600px]">
              {category.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  slug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {category.videos.map((video) => (
              <div
                key={video.id}
                className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative w-full h-[180px] md:h-[200px] overflow-hidden">
                  <img
                    src={video.image}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                      {video.tag}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-75">
                      <i className="ri-play-fill text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-foreground-900 group-hover:text-primary-600 text-sm transition-colors line-clamp-2 mb-2">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-xs"></i> {video.rating}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {video.views}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-100/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
          <p className="text-foreground-500 text-sm mb-4">
            17.c 起草在线观看免费版每日更新大量{categories.find(c => c.slug === slug)?.name || "影视"}内容
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link
              to="/category/recommend"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览热门推荐
            </Link>
            <Link
              to="/"
              className="px-6 py-2.5 bg-background-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}